import { useState } from "react";
import { Combobox, useCombobox } from "@mantine/core";
import { InlineRow } from "./InlineRow";

export interface InlineOption {
  value: string;
  label: string;
}

interface InlineSelectProps {
  label: string;
  value: string | null;
  data: InlineOption[];
  onChange: (value: string | null) => void;
  /** Grey text shown on the right when nothing is selected. */
  placeholder?: string;
  searchable?: boolean;
  disabled?: boolean;
  required?: boolean;
  /** Fixed label-column width so values line up with sibling rows (see InlineRow). */
  labelWidth?: number | string;
  /**
   * Always-present extra option (e.g. "[Не указан]"): shown first when the
   * search box is empty and last while searching, never filtered out.
   */
  pinned?: InlineOption;
}

/**
 * Notion/Linear-style property row: a label on the left and the current value
 * (grey placeholder when empty) on the right; the whole row highlights on hover
 * to signal it's interactive, and clicking it opens a dropdown to choose. This
 * is the single shared standard for inline pickers — restyle it here to restyle
 * them everywhere.
 */
export function InlineSelect({
  label,
  value,
  data,
  onChange,
  placeholder = "Не выбран",
  searchable,
  disabled,
  required,
  labelWidth,
  pinned,
}: InlineSelectProps) {
  const [search, setSearch] = useState("");
  const combobox = useCombobox({
    onDropdownOpen: () => {
      if (searchable) combobox.focusSearchInput();
    },
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch("");
    },
  });

  const query = search.trim().toLowerCase();
  const filtered = searchable && query
    ? data.filter(o => o.label.toLowerCase().includes(query))
    : data;
  const ordered = pinned
    ? (query ? [...filtered, pinned] : [pinned, ...filtered])
    : filtered;

  const selectedLabel =
    value == null
      ? null
      : pinned && value === pinned.value
        ? pinned.label
        : data.find(o => o.value === value)?.label ?? null;

  const optionNodes = ordered.map(o => (
    <Combobox.Option value={o.value} key={o.value} active={o.value === value}>
      {o.label}
    </Combobox.Option>
  ));

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={val => {
        onChange(val);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <InlineRow
          label={label}
          value={selectedLabel}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          labelWidth={labelWidth}
          onClick={() => !disabled && combobox.toggleDropdown()}
        />
      </Combobox.Target>
      <Combobox.Dropdown>
        {searchable && (
          <Combobox.Search
            value={search}
            onChange={e => setSearch(e.currentTarget.value)}
            placeholder="Поиск"
          />
        )}
        <Combobox.Options style={{ maxHeight: 280, overflowY: "auto" }}>
          {optionNodes.length ? optionNodes : <Combobox.Empty>Ничего не найдено</Combobox.Empty>}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
